import AsyncStorage from '@react-native-async-storage/async-storage';
import DeviceInfo from 'react-native-device-info';

import { createLogger } from '../utils/logger';

const logger = createLogger('deviceTrust');
interface TrustedDevice {
  deviceId: string;
  deviceName: string;
  trustedAt: number;
  expiresAt: number;
}

class DeviceTrustService {
  private static TRUST_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
  private static STORAGE_KEY = '@trusted_devices';

  /**
   * Get unique device identifier
   */
  async getDeviceId(): Promise<string> {
    try {
      const deviceId = await DeviceInfo.getUniqueId();
      return deviceId;
    } catch (error) {
      logger.error('Failed to get device ID:', error);
      // Fallback to a generated ID stored in AsyncStorage
      const fallbackId = await AsyncStorage.getItem('@device_fallback_id');
      if (fallbackId) {
        return fallbackId;
      }
      const newId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      await AsyncStorage.setItem('@device_fallback_id', newId);
      return newId;
    }
  }

  /**
   * Get device name for display
   */
  async getDeviceName(): Promise<string> {
    try {
      const deviceName = await DeviceInfo.getDeviceName();
      const model = DeviceInfo.getModel();
      return `${deviceName} (${model})`;
    } catch (error) {
      logger.error('Failed to get device name:', error);
      return 'Unknown Device';
    }
  }

  /**
   * Trust the current device for 30 days
   */
  async trustCurrentDevice(): Promise<void> {
    try {
      const deviceId = await this.getDeviceId();
      const deviceName = await this.getDeviceName();

      const trustedDevice: TrustedDevice = {
        deviceId,
        deviceName,
        trustedAt: Date.now(),
        expiresAt: Date.now() + DeviceTrustService.TRUST_DURATION,
      };

      await AsyncStorage.setItem(
        DeviceTrustService.STORAGE_KEY,
        JSON.stringify(trustedDevice)
      );

      logger.debug('Device trusted:', trustedDevice);
    } catch (error) {
      logger.error('Failed to trust device:', error);
      throw error;
    }
  }

  /**
   * Check if the current device is trusted and not expired
   */
  async isDeviceTrusted(): Promise<boolean> {
    try {
      const currentDeviceId = await this.getDeviceId();
      const trustedDeviceJson = await AsyncStorage.getItem(
        DeviceTrustService.STORAGE_KEY
      );

      if (!trustedDeviceJson) {
        return false;
      }

      const trustedDevice: TrustedDevice = JSON.parse(trustedDeviceJson);

      // Check if device ID matches
      if (trustedDevice.deviceId !== currentDeviceId) {
        return false;
      }

      // Check if trust has expired
      if (Date.now() > trustedDevice.expiresAt) {
        // Clean up expired trust
        await this.revokeTrust();
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Failed to check device trust:', error);
      return false;
    }
  }

  /**
   * Get trusted device information
   */
  async getTrustedDeviceInfo(): Promise<TrustedDevice | null> {
    try {
      const trustedDeviceJson = await AsyncStorage.getItem(
        DeviceTrustService.STORAGE_KEY
      );

      if (!trustedDeviceJson) {
        return null;
      }

      const trustedDevice: TrustedDevice = JSON.parse(trustedDeviceJson);

      // Check if expired
      if (Date.now() > trustedDevice.expiresAt) {
        await this.revokeTrust();
        return null;
      }

      return trustedDevice;
    } catch (error) {
      logger.error('Failed to get trusted device info:', error);
      return null;
    }
  }

  /**
   * Revoke trust for the current device
   */
  async revokeTrust(): Promise<void> {
    try {
      await AsyncStorage.removeItem(DeviceTrustService.STORAGE_KEY);
      logger.debug('Device trust revoked');
    } catch (error) {
      logger.error('Failed to revoke device trust:', error);
    }
  }

  /**
   * Get days remaining until trust expires
   */
  async getDaysUntilExpiry(): Promise<number | null> {
    try {
      const trustedDevice = await this.getTrustedDeviceInfo();

      if (!trustedDevice) {
        return null;
      }

      const daysRemaining = Math.ceil(
        (trustedDevice.expiresAt - Date.now()) / (24 * 60 * 60 * 1000)
      );

      return daysRemaining;
    } catch (error) {
      logger.error('Failed to get days until expiry:', error);
      return null;
    }
  }

  /**
   * Check if 2FA should be required based on device trust
   */
  async should2FABeRequired(): Promise<boolean> {
    const isTrusted = await this.isDeviceTrusted();
    return !isTrusted;
  }
}

export default new DeviceTrustService();
